import { useLayoutEffect } from '@react-aria/utils';
import { type ForwardedRef, type ReactNode, useState } from 'react';
import { useFocusRing, mergeProps, useObjectRef } from 'react-aria';
import { forwardRef } from '../../../forward-ref.js';
import type { NativeElementProps } from '../../types.js';

interface FocusableContainerProps extends NativeElementProps<HTMLDivElement> {
  /** The contents of the container. */
  children: ReactNode;
  /** The expected scrolling direction. */
  scrollDirection: 'horizontal' | 'vertical';
}

function FocusableContainer(
  { children, scrollDirection, ...props }: FocusableContainerProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const containerRef = useObjectRef(ref);
  const [isScrollable, setIsScrollable] = useState(false);
  const { focusProps, isFocusVisible } = useFocusRing();

  useLayoutEffect(() => {
    const containerElement = containerRef.current;
    const isSSR = typeof window === 'undefined';

    let containerResizeObserver: ResizeObserver | undefined;

    if (containerElement && !isSSR) {
      containerResizeObserver = new window.ResizeObserver((entries) => {
        if (entries[0]) {
          const { target } = entries[0];
          setIsScrollable(
            scrollDirection === 'horizontal'
              ? target.scrollWidth > target.clientWidth
              : target.scrollHeight > target.clientHeight,
          );
        }
      });

      containerResizeObserver.observe(containerElement);
    }

    return () => {
      if (containerElement && containerResizeObserver) {
        containerResizeObserver.unobserve(containerElement);
      }
    };
  }, [containerRef, scrollDirection]);

  const containerProps: NativeElementProps<HTMLDivElement> = {
    className: 'cim-focusable-container',
    tabIndex: isScrollable ? 0 : -1,
    'data-focus-visible': isFocusVisible || undefined,
    'data-direction': scrollDirection,
  };

  return (
    <div {...mergeProps(props, focusProps, containerProps)} ref={containerRef}>
      {children}
    </div>
  );
}

// TODO: export this component from the package?
/**
 * Renders a container that can be focused if it becomes scrollable.
 * @internal
 */
const _FocusableContainer = forwardRef(FocusableContainer, 'FocusableContainer');

export { _FocusableContainer as FocusableContainer };
